import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import { getEnv } from 'mobx-state-tree';
import { useNavigate } from 'react-router-dom';

import { RoutePaths } from '@core/routes/RoutePaths';
import { IStoresEnv } from '@core/storesEnv';
import { useAnalyticsGridUIStore, useUserPermissionsUIStore } from '@core/useStores';
import { Button } from '@shared/components/Button';
import { USERS_IN_GROUP_ID } from '@shared/constants';
import { ThemeProps } from '@styles/theme';
import { getExecutionLifecycleState, getStepCompletionStatus } from 'NeoAnalytics/executionSteps';
import { performGetActorsSummary, performQueryActors } from 'NeoAnalytics/services/getActor';
import { performGetEntity } from 'NeoAnalytics/services/getEntities';
import { performGetExecutions, performGetExecutionsFullData } from 'NeoAnalytics/services/getExecutions';
import { performGetGradings } from 'NeoAnalytics/services/getGradings';
import { performGetUsers } from 'Users/services/getUsers';

import { useStyles } from './NeoAnalyticsOverview.styles';

type RangeKey = '7d' | '30d' | '90d' | 'all';

type DashboardExecution = {
    id: string;
    entityId: string;
    entityName: string;
    entityVersion: string | null;
    executionMode: string;
    platform: string;
    startTime: string | null;
    endTime: string | null;
    durationSeconds: number | null;
    actorIds: string[];
    endReason: string | null;
    isCompleted: boolean | null;
    success: boolean | null;
};

type DashboardActor = {
    id: string;
    displayName: string;
    email: string | null;
    roles: string[];
};

type DashboardGrading = {
    executionId: string;
    isReviewed: boolean;
    reviewCount: number;
};

type DashboardActorSummary = {
    actorId: string;
    displayName: string;
    totalExecutions: number;
};

type ProcedureDetail = {
    id: string;
    version: string | null;
    description: string | null;
};

type IssueMetric = {
    name: string;
    entityId?: string | null;
    executionId?: string | null;
    count: number;
    averageDuration?: number | null;
    failureRate?: number | null;
};

type DashboardIssues = {
    longestSteps: IssueMetric[];
    failedSteps: IssueMetric[];
    failingProcedures: IssueMetric[];
    abortedProcedures: IssueMetric[];
    abandonedProcedures: IssueMetric[];
    abandonedSteps: IssueMetric[];
};

type DashboardPayload = {
    executions: DashboardExecution[];
    actors: DashboardActor[];
    gradings: DashboardGrading[];
    actorSummaries: DashboardActorSummary[];
    entityDetails: Record<string, ProcedureDetail>;
    issues: DashboardIssues;
    loadedAt: Date;
};

type DashboardState = {
    isLoading: boolean;
    error: string | null;
    payload: DashboardPayload | null;
    issuesLoading: boolean;
};

type ActivityPoint = {
    key: string;
    label: string;
    value: number;
    completed: number;
    failed: number;
    aborted: number;
    abandoned: number;
    ongoing: number;
    executionIds: string[];
};

type OngoingExecutionCard = {
    id: string;
    entityId: string;
    entityName: string;
    version: string | null;
    actorLabel: string;
    executionMode: string;
    platform: string;
    startTime: string | null;
    elapsedSeconds: number | null;
    actorContext: ExecutionActorContext | null;
};

type ExecutionActorContext = {
    displayName: string | null;
    roleName: string | null;
    email: string | null;
};

const EMPTY_ISSUES: DashboardIssues = {
    longestSteps: [],
    failedSteps: [],
    failingProcedures: [],
    abortedProcedures: [],
    abandonedProcedures: [],
    abandonedSteps: [],
};

const RANGE_OPTIONS: Array<{ key: RangeKey; label: string }> = [
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: 'all', label: 'All time' },
];

function getString(value: unknown): string | null {
    if (typeof value === 'string') {
        const trimmed = value.trim();

        return trimmed.length ? trimmed : null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    return null;
}

function getNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

function getBoolean(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes'].includes(normalized)) {return true;}
        if (['false', '0', 'no'].includes(normalized)) {return false;}
    }

    return null;
}

function asArray<T = Record<string, any>>(value: unknown): T[] {
    if (Array.isArray(value)) {
        return value as T[];
    }

    if (Array.isArray((value as any)?.entities)) {
        return (value as any).entities as T[];
    }

    if (Array.isArray((value as any)?.data)) {
        return (value as any).data as T[];
    }

    return [];
}

function parseDurationToSeconds(duration: string | null, startTime?: string | null, endTime?: string | null): number | null {
    if (duration) {
        const match = duration.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);

        if (match) {
            const hours = Number(match[1]);
            const minutes = Number(match[2]);
            const seconds = Number(match[3]);
            const fraction = match[4] ? Number(`0.${match[4]}`) : 0;

            return hours * 3600 + minutes * 60 + seconds + fraction;
        }
    }

    const start = Date.parse(startTime ?? '');
    const end = Date.parse(endTime ?? '');

    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
        return (end - start) / 1000;
    }

    return null;
}

function formatCompactNumber(value: number): string {
    return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: value >= 1000 ? 1 : 0 }).format(value);
}

function formatWholePercent(value: number): string {
    return `${Math.round(value)}%`;
}

function formatDuration(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return 'n/a';
    }

    if (value < 60) {
        return `${Math.round(value)} sec`;
    }

    if (value < 3600) {
        return `${Math.round(value / 60)} min`;
    }

    return `${(value / 3600).toFixed(1)} h`;
}

function formatDateLabel(value: string | null): string {
    const parsed = Date.parse(value ?? '');

    if (!Number.isFinite(parsed)) {
        return 'Unknown time';
    }

    return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsed);
}

function formatSubtleDateLabel(value: string): string {
    const parsed = Date.parse(value);

    if (!Number.isFinite(parsed)) {
        return 'just now';
    }

    return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsed);
}

function startOfDay(date: Date) {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);

    return result;
}

function getRangeStart(range: RangeKey): Date | null {
    if (range === 'all') {
        return null;
    }

    const now = new Date();
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const from = new Date(now.getTime() - (days - 1) * 24 * 60 * 60 * 1000);

    return startOfDay(from);
}

function getRangeDays(range: RangeKey): number {
    if (range === '7d') {return 7;}
    if (range === '30d') {return 30;}
    if (range === '90d') {return 90;}

    return 30;
}

function formatLocalDateTime(value: Date): string {
    const pad = (part: number) => String(part).padStart(2, '0');

    return [
        `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`,
        `${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`,
    ].join('T');
}

function normalizeExecution(raw: Record<string, any>): DashboardExecution {
    return {
        id: getString(raw.id) ?? '',
        entityId: getString(raw.entityId) ?? '',
        entityName: getString(raw.entityName) ?? 'Unknown procedure',
        entityVersion: getString(raw.entityVersion),
        executionMode: getString(raw.executionMode) ?? 'Unknown',
        platform: getString(raw.platform) ?? 'Unknown',
        startTime: getString(raw.startTime),
        endTime: getString(raw.endTime),
        durationSeconds: parseDurationToSeconds(getString(raw.duration), getString(raw.startTime), getString(raw.endTime)),
        actorIds: Array.isArray(raw.actorIds)
            ? raw.actorIds.map(actorId => getString(actorId)).filter((actorId): actorId is string => Boolean(actorId))
            : [],
        endReason: getString(raw.endReason),
        isCompleted: getBoolean(raw.isCompleted),
        success: getBoolean(raw.success),
    };
}

function normalizeActor(raw: Record<string, any>): DashboardActor {
    const displayName = getString(raw.displayName)
        ?? [getString(raw.firstName), getString(raw.lastName)].filter(Boolean).join(' ')
        ?? getString(raw.email)
        ?? 'Unknown actor';

    return {
        id: getString(raw.id) ?? getString(raw.actorId) ?? '',
        displayName,
        email: getString(raw.email),
        roles: Array.isArray(raw.roles)
            ? raw.roles.map(role => typeof role === 'string' ? role : getString((role as any)?.name)).filter((role): role is string => Boolean(role))
            : [],
    };
}

function normalizeGrading(raw: Record<string, any>): DashboardGrading {
    const reviewCount = Array.isArray(raw.reviews) ? raw.reviews.length : (getNumber(raw.reviewCount) ?? 0);

    return {
        executionId: getString(raw.executionId) ?? '',
        isReviewed: Boolean(getBoolean(raw.isReviewed)) || reviewCount > 0,
        reviewCount,
    };
}

function normalizeActorSummary(raw: Record<string, any>): DashboardActorSummary {
    return {
        actorId: getString(raw.actorId) ?? getString(raw.id) ?? '',
        displayName: getString(raw.displayName)
            ?? [getString(raw.firstName), getString(raw.lastName)].filter(Boolean).join(' ')
            ?? 'Unknown actor',
        totalExecutions: getNumber(raw.totalExecutions)
            ?? getNumber(raw.executionsCount)
            ?? getNumber(raw.executionCount)
            ?? getNumber(raw.totalSessions)
            ?? 0,
    };
}

function getStatusTone(status: string, theme: ThemeProps) {
    switch (status) {
                    case 'Completed':
                        return { color: theme.colors.green, background: 'rgba(16, 185, 129, 0.14)' };
                    case 'Failed':
                    case 'Exception':
                        return { color: theme.colors.red, background: 'rgba(220, 38, 38, 0.12)' };
                    case 'Stopped':
                        return { color: theme.isDark ? '#e8a060' : '#7a5b00', background: 'rgba(245, 212, 81, 0.22)' };
                    case 'Abandoned':
                        return { color: theme.colors.grey_text, background: 'rgba(102, 119, 128, 0.14)' };
                    default:
                        return { color: '#d18100', background: 'rgba(234, 179, 8, 0.18)' };
    }
}

function getExecutionStatusLabel(execution: DashboardExecution): string {
    const lifecycle = getExecutionLifecycleState(execution);
    const normalizedEndReason = String(execution.endReason ?? '').toLowerCase();

    if (lifecycle === 'failed') {
        return normalizedEndReason.includes('exception') || execution.isCompleted === false ? 'Exception' : 'Failed';
    }

    if (lifecycle === 'aborted') {return 'Stopped';}
    if (lifecycle === 'abandoned') {return 'Abandoned';}
    if (lifecycle === 'ongoing') {return 'In Progress';}

    return 'Completed';
}

function buildIssues(fullDataExecutions: Array<Record<string, any>>): DashboardIssues {
    const stepMap = new Map<string, {
        count: number;
        durationTotal: number;
        failures: number;
        latestExecutionId: string | null;
        latestEntityId: string | null;
        latestTimestamp: number;
        latestFailedExecutionId: string | null;
        latestFailedEntityId: string | null;
        latestFailedTimestamp: number;
    }>();
    const procedureFailed = new Map<string, { entityId: string | null; count: number }>();
    const procedureAborted = new Map<string, { entityId: string | null; count: number }>();
    const procedureAbandoned = new Map<string, { entityId: string | null; count: number }>();
    const abandonedStepCounts = new Map<string, { count: number; entityId: string | null; executionId: string | null }>();

    fullDataExecutions.forEach(execution => {
        const executionName = getString(execution.entityName) ?? 'Unknown procedure';
        const entityId = getString(execution.entityId);
        const executionRecordId = getString(execution.id);
        const executionTimestamp = Date.parse(getString(execution.startTime) ?? '');
        const lifecycle = getExecutionLifecycleState({
            endTime: getString(execution.endTime),
            endReason: getString(execution.endReason),
            isCompleted: getBoolean(execution.isCompleted),
            success: getBoolean(execution.success),
            startTime: getString(execution.startTime),
        });

        if (lifecycle === 'failed') {
            const aggregate = procedureFailed.get(executionName) ?? { entityId, count: 0 };
            aggregate.count += 1;
            procedureFailed.set(executionName, aggregate);
        }

        if (lifecycle === 'aborted') {
            const aggregate = procedureAborted.get(executionName) ?? { entityId, count: 0 };
            aggregate.count += 1;
            procedureAborted.set(executionName, aggregate);
        }

        if (lifecycle === 'abandoned') {
            const aggregate = procedureAbandoned.get(executionName) ?? { entityId, count: 0 };
            aggregate.count += 1;
            procedureAbandoned.set(executionName, aggregate);

            const steps = Array.isArray(execution.steps) ? execution.steps : [];
            if (steps.length > 0) {
                const lastStep = steps[steps.length - 1];
                const stepName = getString(lastStep?.name) ?? 'Unnamed step';
                const existing = abandonedStepCounts.get(stepName) ?? { count: 0, entityId, executionId: executionRecordId };
                existing.count += 1;
                abandonedStepCounts.set(stepName, existing);
            }
        }

        const steps = Array.isArray(execution.steps) ? execution.steps : [];
        steps.forEach(step => {
            const name = getString(step?.name) ?? 'Unnamed step';
            const duration = parseDurationToSeconds(
                getString(step?.duration),
                getString(step?.startTime),
                getString(step?.endTime),
            );
            const status = getStepCompletionStatus(step);
            const aggregate = stepMap.get(name) ?? {
                count: 0,
                durationTotal: 0,
                failures: 0,
                latestExecutionId: null,
                latestEntityId: null,
                latestTimestamp: Number.NEGATIVE_INFINITY,
                latestFailedExecutionId: null,
                latestFailedEntityId: null,
                latestFailedTimestamp: Number.NEGATIVE_INFINITY,
            };

            aggregate.count += 1;
            if (duration !== null) {
                aggregate.durationTotal += duration;
            }
            if (Number.isFinite(executionTimestamp) && executionTimestamp >= aggregate.latestTimestamp) {
                aggregate.latestTimestamp = executionTimestamp;
                aggregate.latestExecutionId = executionRecordId;
                aggregate.latestEntityId = entityId;
            }
            if (status === 'failed' || status === 'aborted') {
                aggregate.failures += 1;
                if (Number.isFinite(executionTimestamp) && executionTimestamp >= aggregate.latestFailedTimestamp) {
                    aggregate.latestFailedTimestamp = executionTimestamp;
                    aggregate.latestFailedExecutionId = executionRecordId;
                    aggregate.latestFailedEntityId = entityId;
                }
            }

            stepMap.set(name, aggregate);
        });
    });

    const longestSteps = Array.from(stepMap.entries())
        .map(([name, aggregate]) => ({
            name,
            count: aggregate.count,
            averageDuration: aggregate.count ? aggregate.durationTotal / aggregate.count : null,
            entityId: aggregate.latestEntityId,
            executionId: aggregate.latestExecutionId,
        }))
        .filter(item => item.averageDuration !== null)
        .sort((left, right) => (right.averageDuration ?? 0) - (left.averageDuration ?? 0))
        .slice(0, 5);

    const failedSteps = Array.from(stepMap.entries())
        .map(([name, aggregate]) => ({
            name,
            count: aggregate.failures,
            failureRate: aggregate.count ? aggregate.failures / aggregate.count : 0,
            entityId: aggregate.latestFailedEntityId ?? aggregate.latestEntityId,
            executionId: aggregate.latestFailedExecutionId ?? aggregate.latestExecutionId,
        }))
        .filter(item => item.count > 0)
        .sort((left, right) => right.count - left.count || (right.failureRate ?? 0) - (left.failureRate ?? 0))
        .slice(0, 5);

    const failingProcedures = Array.from(procedureFailed.entries())
        .map(([name, aggregate]) => ({ name, entityId: aggregate.entityId, count: aggregate.count }))
        .sort((left, right) => right.count - left.count)
        .slice(0, 5);

    const abortedProcedures = Array.from(procedureAborted.entries())
        .map(([name, aggregate]) => ({ name, entityId: aggregate.entityId, count: aggregate.count }))
        .sort((left, right) => right.count - left.count)
        .slice(0, 5);

    const abandonedProcedures = Array.from(procedureAbandoned.entries())
        .map(([name, aggregate]) => ({ name, entityId: aggregate.entityId, count: aggregate.count }))
        .sort((left, right) => right.count - left.count)
        .slice(0, 5);

    const abandonedSteps = Array.from(abandonedStepCounts.entries())
        .map(([name, data]) => ({ name, count: data.count, entityId: data.entityId, executionId: data.executionId }))
        .sort((left, right) => right.count - left.count)
        .slice(0, 5);

    return {
        longestSteps,
        failedSteps,
        failingProcedures,
        abortedProcedures,
        abandonedProcedures,
        abandonedSteps,
    };
}

function buildDashboardModel(payload: DashboardPayload | null) {
    if (!payload) {
        return null;
    }

    const { executions, actors, gradings, actorSummaries, entityDetails, loadedAt, issues } = payload;
    const actorMap = new Map(actors.map(actor => [actor.id, actor]));
    const uniqueActorIds = new Set(executions.flatMap(execution => execution.actorIds));
    const uniqueProcedureIds = new Set(executions.map(execution => execution.entityId).filter(Boolean));
    const gradingByExecutionId = new Map(gradings.map(grading => [grading.executionId, grading]));

    const statusCounters = {
        completed: 0,
        failed: 0,
        aborted: 0,
        abandoned: 0,
        ongoing: 0,
    };

    executions.forEach(execution => {
        const lifecycle = getExecutionLifecycleState(execution);
        if (lifecycle === 'completed') {statusCounters.completed += 1;}
        if (lifecycle === 'failed') {statusCounters.failed += 1;}
        if (lifecycle === 'aborted') {statusCounters.aborted += 1;}
        if (lifecycle === 'abandoned') {statusCounters.abandoned += 1;}
        if (lifecycle === 'ongoing') {statusCounters.ongoing += 1;}
    });

    const durations = executions.map(execution => execution.durationSeconds).filter((duration): duration is number => duration !== null);
    const avgDuration = durations.length ? durations.reduce((sum, value) => sum + value, 0) / durations.length : null;
    const gradedCount = gradings.length;
    const reviewedCount = gradings.filter(grading => grading.isReviewed || grading.reviewCount > 0).length;
    const completionRate = executions.length ? (statusCounters.completed / executions.length) * 100 : 0;
    const gradingCoverage = executions.length ? (gradedCount / executions.length) * 100 : 0;

    const topProcedures = Array.from(uniqueProcedureIds).map(entityId => {
        const procedureExecutions = executions.filter(execution => execution.entityId === entityId);
        const averageDuration = procedureExecutions
            .map(execution => execution.durationSeconds)
            .filter((duration): duration is number => duration !== null);

        return {
            entityId,
            name: procedureExecutions[0]?.entityName ?? 'Unknown procedure',
            version: entityDetails[entityId]?.version ?? procedureExecutions[0]?.entityVersion ?? null,
            count: procedureExecutions.length,
            avgDuration: averageDuration.length ? averageDuration.reduce((sum, value) => sum + value, 0) / averageDuration.length : null,
        };
    }).sort((left, right) => right.count - left.count).slice(0, 5);

    const actorCounts = new Map<string, number>();
    executions.forEach(execution => {
        execution.actorIds.forEach(actorId => actorCounts.set(actorId, (actorCounts.get(actorId) ?? 0) + 1));
    });

    const topActors = (actorSummaries.length ? actorSummaries : Array.from(actorCounts.entries()).map(([actorId, totalExecutions]) => ({
        actorId,
        displayName: actorMap.get(actorId)?.displayName ?? 'Unknown actor',
        totalExecutions,
    })))
        .map(item => ({
            ...item,
            displayName: item.displayName || actorMap.get(item.actorId)?.displayName || 'Unknown actor',
            email: actorMap.get(item.actorId)?.email ?? null,
            roles: actorMap.get(item.actorId)?.roles ?? [],
        }))
        .sort((left, right) => right.totalExecutions - left.totalExecutions)
        .slice(0, 5);

    const modeMix = Object.entries(executions.reduce<Record<string, number>>((accumulator, execution) => {
        const key = execution.executionMode || 'Unknown';
        accumulator[key] = (accumulator[key] ?? 0) + 1;

        return accumulator;
    }, {})).sort((left, right) => right[1] - left[1]).slice(0, 5);

    const platformMix = Object.entries(executions.reduce<Record<string, number>>((accumulator, execution) => {
        const key = execution.platform || 'Unknown';
        accumulator[key] = (accumulator[key] ?? 0) + 1;

        return accumulator;
    }, {})).sort((left, right) => right[1] - left[1]).slice(0, 5);

    const recentExecutions = executions
        .slice()
        .sort((left, right) => Date.parse(right.startTime ?? '') - Date.parse(left.startTime ?? ''))
        .slice(0, 6)
        .map(execution => {
            const grading = gradingByExecutionId.get(execution.id);
            const actorNames = execution.actorIds.map(actorId => actorMap.get(actorId)?.displayName).filter((name): name is string => Boolean(name));
            const primaryActor = execution.actorIds.map(actorId => actorMap.get(actorId)).find(Boolean) ?? null;

            return {
                ...execution,
                statusLabel: getExecutionStatusLabel(execution),
                actorLabel: actorNames.length ? actorNames.join(', ') : 'No actor mapped',
                grading,
                actorContext: primaryActor ? {
                    displayName: primaryActor.displayName ?? null,
                    roleName: primaryActor.roles[0] ?? null,
                    email: primaryActor.email ?? null,
                } satisfies ExecutionActorContext : null,
            };
        });

    const ongoingExecutions = executions
        .filter(execution => getExecutionLifecycleState(execution) === 'ongoing')
        .sort((left, right) => Date.parse(right.startTime ?? '') - Date.parse(left.startTime ?? ''))
        .map(execution => {
            const actorNames = execution.actorIds
                .map(actorId => actorMap.get(actorId)?.displayName)
                .filter((name): name is string => Boolean(name));
            const primaryActor = execution.actorIds.map(actorId => actorMap.get(actorId)).find(Boolean) ?? null;
            const startTime = Date.parse(execution.startTime ?? '');

            return {
                id: execution.id,
                entityId: execution.entityId,
                entityName: execution.entityName,
                version: entityDetails[execution.entityId]?.version ?? execution.entityVersion ?? null,
                actorLabel: actorNames.length ? actorNames.join(', ') : 'No actor mapped',
                executionMode: execution.executionMode,
                platform: execution.platform,
                startTime: execution.startTime,
                elapsedSeconds: Number.isFinite(startTime) ? Math.max(0, (loadedAt.getTime() - startTime) / 1000) : null,
                actorContext: primaryActor ? {
                    displayName: primaryActor.displayName ?? null,
                    roleName: primaryActor.roles[0] ?? null,
                    email: primaryActor.email ?? null,
                } satisfies ExecutionActorContext : null,
            } satisfies OngoingExecutionCard;
        });

    return {
        loadedAt,
        totalExecutions: executions.length,
        completionRate,
        avgDuration,
        uniqueActorCount: uniqueActorIds.size,
        uniqueProcedureCount: uniqueProcedureIds.size,
        gradedCount,
        reviewedCount,
        gradingCoverage,
        statusCounters,
        topProcedures,
        topActors,
        modeMix,
        platformMix,
        recentExecutions,
        ongoingExecutions,
        spotlightProcedure: topProcedures[0] ?? null,
        issues,
    };
}

export const NeoAnalyticsOverview: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const analyticsGridUIStore = useAnalyticsGridUIStore();
    const userPermissionsUIStore = useUserPermissionsUIStore();
    const { api, filtersOptions } = getEnv<IStoresEnv>(analyticsGridUIStore);
    const navigate = useNavigate();

    const [range, setRange] = useState<RangeKey>('30d');
    const [reloadKey, setReloadKey] = useState(0);
    const [pulseBreakdown, setPulseBreakdown] = useState(true);
    const [pulseFilters, setPulseFilters] = useState<Set<string>>(() => new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']));
    const [popupFilters, setPopupFilters] = useState<Set<string>>(() => new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']));
    const [pulseBarModal, setPulseBarModal] = useState<{ point: ActivityPoint; anchor: { top: number; left: number; width: number; height: number } } | null>(null);
    useEffect(() => {
        setPopupFilters(new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']));
    }, [pulseBarModal?.point.key]);
    const [selectedGroupId, setSelectedGroupId] = useState<string>(
        () => sessionStorage.getItem('neo-analytics-overview-group') ?? '',
    );

    const handleGroupChange = (value: string) => {
        if (value) {
            sessionStorage.setItem('neo-analytics-overview-group', value);
        } else {
            sessionStorage.removeItem('neo-analytics-overview-group');
        }
        setSelectedGroupId(value);
    };
    const [lastPayload, setLastPayload] = useState<DashboardPayload | null>(null);
    const [state, setState] = useState<DashboardState>({
        isLoading: true,
        error: null,
        payload: null,
        issuesLoading: false,
    });

    const renderLoadingPlaceholder = () => (
        <div className={styles.wrapper}>
            {/* <div className={styles.loadingIntro}>
                <div className={styles.loadingEyebrow}>Neo Analytics</div>
                <div className={styles.loadingTitle}>Execution signals are taking shape.</div>
                <div className={styles.loadingBars}>
                    <span className={styles.loadingBarTall} />
                    <span className={styles.loadingBarMedium} />
                    <span className={styles.loadingBarShort} />
                </div>
            </div> */}
            <div className={styles.topBar}>
                <Skeleton variant="rounded" width={150} height={38} />
                <Skeleton variant="rounded" width={320} height={40} />
            </div>
            <div className={styles.hero}>
                <div className={styles.heroHeader}>
                    <Skeleton variant="text" width={120} height={18} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
                    <Skeleton variant="text" width="62%" height={56} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
                </div>
                <div className={styles.heroMetaGrid}>
                    {[0, 1, 2].map(index => (
                        <div key={index} className={styles.heroMetaCard}>
                            <Skeleton variant="text" width="35%" height={18} sx={{ bgcolor: 'rgba(255,255,255,0.28)' }} />
                            <Skeleton variant="text" width="80%" height={38} sx={{ bgcolor: 'rgba(255,255,255,0.28)' }} />
                            <Skeleton variant="text" width="70%" height={18} sx={{ bgcolor: 'rgba(255,255,255,0.28)' }} />
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.summaryGrid}>
                {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className={styles.summaryCard}>
                        <Skeleton variant="text" width="40%" height={18} />
                        <Skeleton variant="text" width="55%" height={38} />
                        <Skeleton variant="text" width="85%" height={18} />
                    </div>
                ))}
            </div>
            {[0, 1, 2].map(row => (
                <div key={row} className={styles.dashboardGrid}>
                    {[0, 1].map(panel => (
                        <div key={panel} className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <div style={{ width: '100%' }}>
                                    <Skeleton variant="text" width="40%" height={24} />
                                    <Skeleton variant="text" width="70%" height={18} />
                                </div>
                            </div>
                            <div className={styles.panelBody}>
                                {Array.from({ length: 4 }).map((__, itemIndex) => (
                                    <Skeleton key={itemIndex} variant="rounded" width="100%" height={48} style={{ marginBottom: 12 }} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );

    useEffect(() => {
        filtersOptions.loadFilterOptions();
    }, [filtersOptions]);

    const groupOptions = useMemo(
        () => filtersOptions.groups.map(group => ({ id: group.id, name: group.name })),
        [filtersOptions.groups],
    );

    const selectedGroupName = useMemo(
        () => groupOptions.find(group => group.id === selectedGroupId)?.name ?? 'All groups',
        [groupOptions, selectedGroupId],
    );

    const model = useMemo(() => buildDashboardModel(state.payload ?? lastPayload), [lastPayload, state.payload]);

    const pulseSeries = useMemo(() => {
        if (!(state.payload ?? lastPayload)) {
            return [] as ActivityPoint[];
        }

        const executions = (state.payload ?? lastPayload)?.executions ?? [];
        const windowDays = getRangeDays(range);
        const bucketSizeDays = windowDays > 31 ? 7 : 1;
        const bucketCount = Math.ceil(windowDays / bucketSizeDays);
        const now = new Date();
        const start = startOfDay(new Date(now.getTime() - (windowDays - 1) * 24 * 60 * 60 * 1000));
        const buckets = Array.from({ length: bucketCount }).map((_, index) => {
            const bucketStart = new Date(start.getTime() + index * bucketSizeDays * 24 * 60 * 60 * 1000);

            return {
                key: bucketStart.toISOString(),
                label: new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(bucketStart),
                value: 0,
                completed: 0,
                failed: 0,
                aborted: 0,
                abandoned: 0,
                ongoing: 0,
                executionIds: [] as string[],
                start: bucketStart.getTime(),
                end: new Date(bucketStart.getTime() + bucketSizeDays * 24 * 60 * 60 * 1000).getTime(),
            };
        });

        executions.forEach(execution => {
            const time = Date.parse(execution.startTime ?? '');

            if (!Number.isFinite(time) || time < start.getTime()) {
                return;
            }

            const bucket = buckets.find(item => time >= item.start && time < item.end);
            if (bucket) {
                bucket.value += 1;
                bucket.executionIds.push(execution.id);
                const lifecycle = getExecutionLifecycleState(execution);
                if (lifecycle === 'completed') {bucket.completed += 1;} else if (lifecycle === 'failed') {bucket.failed += 1;} else if (lifecycle === 'aborted') {bucket.aborted += 1;} else if (lifecycle === 'abandoned') {bucket.abandoned += 1;} else {bucket.ongoing += 1;}
            }
        });

        return buckets.map(({ key, label, value, completed, failed, aborted, abandoned, ongoing, executionIds }) => ({ key, label, value, completed, failed, aborted, abandoned, ongoing, executionIds }));
    }, [lastPayload, range, state.payload]);

    const filteredPulseSeries = useMemo(() =>
        pulseSeries.map(entry => {
            const completed = pulseFilters.has('completed') ? entry.completed : 0;
            const failed = pulseFilters.has('failed') ? entry.failed : 0;
            const aborted = pulseFilters.has('aborted') ? entry.aborted : 0;
            const abandoned = pulseFilters.has('abandoned') ? entry.abandoned : 0;
            const ongoing = pulseFilters.has('ongoing') ? entry.ongoing : 0;
            const value = completed + failed + aborted + abandoned + ongoing;

            return { ...entry, value, completed, failed, aborted, abandoned, ongoing };
        }),
    [pulseSeries, pulseFilters],
    );

    const maxFilteredPulseValue = Math.max(...filteredPulseSeries.map(p => p.value), 1);

    const pulseTitle = range === 'all'
        ? 'Activity pulse: last 30 days'
        : `Activity pulse: ${RANGE_OPTIONS.find(option => option.key === range)?.label ?? range}`;

    useEffect(() => {
        let cancelled = false;

        const loadDashboard = async () => {
            setState({ isLoading: true, error: null, payload: null, issuesLoading: false });

            let scopedActorIds: string[] | undefined;

            if (selectedGroupId) {
                const usersResult = await performGetUsers(api, {
                    payload: {
                        page: 0,
                        limit: 1000,
                        searchText: '',
                        sortDirection: undefined,
                        sortFieldName: undefined,
                        departments: [],
                        roleIds: [],
                        statusIds: [],
                        groupIds: [],
                        procedureIds: [],
                        lastLoginStart: undefined,
                        inGroup: USERS_IN_GROUP_ID,
                        inGroupId: selectedGroupId,
                    },
                    errorHandlers: { DEFAULT: '' },
                });

                if (!usersResult.success) {
                    if (!cancelled) {
                        setState({ isLoading: false, error: 'Unable to resolve the selected group members for Neo Analytics.', payload: null, issuesLoading: false });
                    }

                    return;
                }

                scopedActorIds = asArray<Record<string, any>>(usersResult.data?.entities ?? usersResult.data)
                    .map(user => getString(user.id))
                    .filter((id): id is string => Boolean(id));

                if (!scopedActorIds.length) {
                    if (!cancelled) {
                        setState({
                            isLoading: false,
                            error: null,
                            payload: {
                                executions: [],
                                actors: [],
                                gradings: [],
                                actorSummaries: [],
                                entityDetails: {},
                                issues: EMPTY_ISSUES,
                                loadedAt: new Date(),
                            },
                            issuesLoading: false,
                        });
                    }

                    return;
                }
            }

            const from = getRangeStart(range);
            const to = new Date();
            const formattedFrom = from ? formatLocalDateTime(from) : undefined;
            const formattedTo = formatLocalDateTime(to);
            const executionPayload = {
                ...(from ? { From: formattedFrom, To: formattedTo } : {}),
                ...(scopedActorIds?.length ? { ActorIds: scopedActorIds } : {}),
            };
            const shouldLoadIssues = range !== 'all' || Boolean(selectedGroupId);

            const [executionsResult, actorsResult, gradingsResult, actorSummaryResult] = await Promise.all([
                performGetExecutions(api, {
                    payload: executionPayload,
                    errorHandlers: { DEFAULT: '' },
                }),
                performQueryActors(api, {
                    payload: scopedActorIds?.length ? { ActorIds: scopedActorIds } : {},
                    errorHandlers: { DEFAULT: '' },
                }),
                performGetGradings(api, {
                    payload: {
                        executionIds: [],
                        ...(from ? { From: formattedFrom, To: formattedTo } : {}),
                        ...(scopedActorIds?.length ? { ActorIds: scopedActorIds } : {}),
                    },
                    errorHandlers: { DEFAULT: '' },
                }),
                performGetActorsSummary(api, {
                    payload: scopedActorIds?.length ? { ActorIds: scopedActorIds } : {},
                    errorHandlers: { DEFAULT: '' },
                }),
            ]);

            if (!executionsResult.success) {
                if (!cancelled) {
                    setState({ isLoading: false, error: 'Unable to load Neo Analytics executions right now.', payload: null, issuesLoading: false });
                }

                return;
            }

            const executions = asArray<Record<string, any>>(executionsResult.data)
                .map(normalizeExecution)
                .filter(execution => execution.id);
            const executionIds = executions.map(execution => execution.id);

            const actors = actorsResult.success
                ? asArray<Record<string, any>>(actorsResult.data).map(normalizeActor).filter(actor => actor.id)
                : [];
            const gradings = gradingsResult.success
                ? asArray<Record<string, any>>(gradingsResult.data)
                    .map(normalizeGrading)
                    .filter(grading => grading.executionId && (!executionIds.length || executionIds.includes(grading.executionId)))
                : [];
            const actorSummaries = actorSummaryResult.success
                ? asArray<Record<string, any>>(actorSummaryResult.data).map(normalizeActorSummary).filter(summary => summary.actorId)
                : [];

            const topEntityIds = Array.from(new Set(executions.map(execution => execution.entityId).filter(Boolean))).slice(0, 5);
            const entityEntries = await Promise.all(topEntityIds.map(async entityId => {
                const result = await performGetEntity(api, {
                    payload: { id: entityId },
                    errorHandlers: { DEFAULT: '' },
                });

                if (!result.success) {
                    return null;
                }

                return [entityId, {
                    id: entityId,
                    version: getString(result.data?.version ?? result.data?.entityVersion),
                    description: getString(result.data?.description ?? result.data?.entityDescription),
                }] as const;
            }));

            if (cancelled) {
                return;
            }

            const loadedAt = new Date();
            const basePayload: DashboardPayload = {
                executions,
                actors,
                gradings,
                actorSummaries,
                entityDetails: Object.fromEntries(entityEntries.filter((entry): entry is readonly [string, ProcedureDetail] => Boolean(entry))),
                issues: EMPTY_ISSUES,
                loadedAt,
            };

            setState({
                isLoading: false,
                error: null,
                payload: basePayload,
                issuesLoading: shouldLoadIssues,
            });
            setLastPayload(basePayload);

            if (!shouldLoadIssues) {
                return;
            }

            const fullDataResult = await performGetExecutionsFullData(api, {
                payload: executionPayload,
                errorHandlers: { DEFAULT: '' },
            });

            if (cancelled) {
                return;
            }

            const issues = fullDataResult.success
                ? buildIssues(asArray<Record<string, any>>(fullDataResult.data))
                : EMPTY_ISSUES;
            const nextPayload: DashboardPayload = {
                ...basePayload,
                issues,
            };

            setState({
                isLoading: false,
                error: null,
                payload: nextPayload,
                issuesLoading: false,
            });
            setLastPayload(nextPayload);
        };

        void loadDashboard();

        return () => {
            cancelled = true;
        };
    }, [api, range, reloadKey, selectedGroupId]);

    if (state.isLoading) {
        return renderLoadingPlaceholder();
    }

    if (state.error) {
        return <div className={styles.errorCard}>{state.error}</div>;
    }

    if (!model || model.totalExecutions === 0) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.topBar}>
                    <Button className={styles.legacyButton} color="secondary" onClick={() => navigate(`/${RoutePaths.analyticsLegacy}`)}>
                        Legacy Analytics
                    </Button>
                    <FormControl className={styles.groupField} size="small">
                        <InputLabel id="neo-analytics-group-select">Group</InputLabel>
                        <Select
                            labelId="neo-analytics-group-select"
                            value={selectedGroupId}
                            label="Group"
                            onChange={event => handleGroupChange(event.target.value)}
                        >
                            <MenuItem value="">All groups</MenuItem>
                            {groupOptions.map(group => (
                                <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={styles.controlsRow}>
                    <div className={styles.rangeControls}>
                        {RANGE_OPTIONS.map(option => (
                            <button
                                key={option.key}
                                type="button"
                                className={styles.rangeButton}
                                data-active={range === option.key}
                                onClick={() => setRange(option.key)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.emptyState}>
                    <div className={styles.emptyTitle}>No executions found for this selection</div>
                    <div className={styles.emptyDescription}>Try a different time window or pick another group. The current page is scoped to {selectedGroupName}.</div>
                </div>
            </div>
        );
    }

    const summaryCards = [
        { label: 'Sessions', value: formatCompactNumber(model.totalExecutions), hint: `${formatWholePercent(model.completionRate)} completed` },
        { label: 'Actors', value: formatCompactNumber(model.uniqueActorCount), hint: 'People active in the current scope' },
        { label: 'Procedures', value: formatCompactNumber(model.uniqueProcedureCount), hint: 'Procedures touched in this window' },
        { label: 'Avg duration', value: formatDuration(model.avgDuration), hint: 'Session duration across the current scope' },
        { label: 'Graded', value: formatCompactNumber(model.gradedCount), hint: `${formatWholePercent(model.gradingCoverage)} of sessions have grading` },
        { label: 'Reviewed', value: formatCompactNumber(model.reviewedCount), hint: 'Sessions with at least one review' },
    ];

    const healthRows = [
        { label: 'Completed', value: model.statusCounters.completed, color: theme.colors.green },
        { label: 'Failed', value: model.statusCounters.failed, color: theme.colors.red },
        { label: 'Stopped', value: model.statusCounters.aborted, color: theme.isDark ? '#e8a060' : '#7a5b00' },
        { label: 'Abandoned', value: model.statusCounters.abandoned, color: '#667780' },
        { label: 'In progress', value: model.statusCounters.ongoing, color: '#d18100' },
    ];

    const visibleTopActors = model.topActors.map(actor => ({
        ...actor,
        visibleRoles: userPermissionsUIStore.canEditUser(actor.roles) ? actor.roles.slice(0, 2) : [],
    }));

    const openExecutionDetails = (entityId?: string | null, executionId?: string | null) => {
        if (!entityId || !executionId) {
            return;
        }

        const execution = model.recentExecutions.find(item => item.id === executionId)
            ?? model.ongoingExecutions.find(item => item.id === executionId);

        navigate(`/${RoutePaths.procedures}/analytics/${entityId}/executions/${executionId}`, {
            state: {
                fromAnalytics: true,
                executionActor: execution?.actorContext ?? null,
            },
        });
    };

    return (
        <>
            <div className={styles.wrapper}>
                <div className={styles.topBar}>
                    <Button className={styles.legacyButton} color="secondary" onClick={() => navigate(`/${RoutePaths.analyticsLegacy}`)}>
                    Legacy Analytics
                    </Button>
                    <div className={styles.topBarActions}>
                        <FormControl className={styles.groupField} size="small">
                            <InputLabel id="neo-analytics-group-select">Group</InputLabel>
                            <Select
                                labelId="neo-analytics-group-select"
                                value={selectedGroupId}
                                label="Group"
                                onChange={event => handleGroupChange(event.target.value)}
                            >
                                <MenuItem value="">All groups</MenuItem>
                                {groupOptions.map(group => (
                                    <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <div className={styles.refreshMeta}>
                            <span className={styles.refreshMetaLabel}>{`Updated ${formatSubtleDateLabel(model.loadedAt.toISOString())}`}</span>
                            <Button className={styles.refreshButton} onClick={() => setReloadKey(current => current + 1)} loading={state.isLoading} color="primary">
                            Refresh
                            </Button>
                        </div>
                    </div>
                </div>

                <div className={styles.hero}>
                    <div className={styles.heroGlow} />
                    <div className={styles.heroHeader}>
                        <div className={styles.heroEyebrow}>Neo Analytics</div>
                        <div className={styles.heroTitle}>Execution health and procedure momentum for {selectedGroupName}.</div>
                    </div>
                    <div className={styles.heroMetaGrid}>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Spotlight procedure</div>
                            <button type="button" className={styles.heroMetaAction} onClick={() => model.spotlightProcedure?.entityId && navigate(`/${RoutePaths.procedures}/analytics/${model.spotlightProcedure.entityId}/insights`, { state: { fromNeoOverview: true } })}>
                                <div className={styles.heroMetaValue}>{model.spotlightProcedure?.name ?? 'No procedure data'}</div>
                            </button>
                            <div className={styles.heroMetaHint}>{model.spotlightProcedure ? `${model.spotlightProcedure.count} sessions${model.spotlightProcedure.version ? ` • v${model.spotlightProcedure.version}` : ''}` : 'No procedure detail available'}</div>
                        </div>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Scope</div>
                            <div className={styles.heroMetaValue}>{selectedGroupName}</div>
                            <div className={styles.heroMetaHint}>{RANGE_OPTIONS.find(option => option.key === range)?.label ?? range}</div>
                        </div>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Failure watch</div>
                            <div className={styles.heroMetaValue}>{formatWholePercent(((model.statusCounters.failed + model.statusCounters.aborted + model.statusCounters.abandoned) / Math.max(model.totalExecutions, 1)) * 100)}</div>
                            <div className={styles.heroMetaHint}>{`${model.statusCounters.failed} failed, ${model.statusCounters.aborted} stopped, and ${model.statusCounters.abandoned} abandoned sessions in scope`}</div>
                        </div>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Most used platform</div>
                            <div className={styles.heroMetaValue}>{model.platformMix[0]?.[0] ?? '—'}</div>
                            <div className={styles.heroMetaHint}>{model.platformMix[0] ? `${formatWholePercent((model.platformMix[0][1] / Math.max(model.totalExecutions, 1)) * 100)} of all sessions` : 'No platform data available'}</div>
                        </div>
                    </div>
                </div>

                <div className={styles.controlsRow}>
                    <div className={styles.rangeControls}>
                        {RANGE_OPTIONS.map(option => (
                            <button
                                key={option.key}
                                type="button"
                                className={styles.rangeButton}
                                data-active={range === option.key}
                                onClick={() => setRange(option.key)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.summaryGrid}>
                    {summaryCards.map(card => (
                        <div key={card.label} className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>{card.label}</div>
                            <div className={styles.summaryValue}>{card.value}</div>
                            <div className={styles.summaryHint}>{card.hint}</div>
                        </div>
                    ))}
                </div>

                {model.ongoingExecutions.length > 0 && (
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Executions in progress</div>
                                <div className={styles.panelSubtitle}>Live sessions are shown only while they remain open in the current scope.</div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.ongoingStrip}>
                                {model.ongoingExecutions.map(execution => (
                                    <button
                                        key={execution.id}
                                        type="button"
                                        className={styles.ongoingCard}
                                        onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${execution.entityId}/executions/${execution.id}`, {
                                            state: {
                                                fromAnalytics: true,
                                                executionActor: execution.actorContext ?? null,
                                            },
                                        })}
                                    >
                                        <div className={styles.ongoingCardHeader}>
                                            <span className={styles.ongoingTitle}>{execution.entityName}</span>
                                            {execution.version && <span className={styles.ongoingBadge}>{`v${execution.version}`}</span>}
                                        </div>
                                        <div className={styles.ongoingMeta}>
                                            <span>{execution.actorLabel}</span>
                                            <span>{execution.executionMode}</span>
                                            <span>{execution.platform}</span>
                                        </div>
                                        <div className={styles.ongoingMeta}>
                                            <span>{`Started ${formatDateLabel(execution.startTime)}`}</span>
                                            <span>{`Elapsed ${formatDuration(execution.elapsedSeconds)}`}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.dashboardGrid}>
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>{pulseTitle}</div>
                                <div className={styles.panelSubtitle}>Recent session volume helps spot bursts, plateaus, and silent periods.</div>
                            </div>
                            <button
                                type="button"
                                className={styles.pulseToggle}
                                data-active={pulseBreakdown}
                                onClick={() => setPulseBreakdown(v => !v)}
                            >
                            Breakdown
                            </button>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.activityBars}>
                                {filteredPulseSeries.map(entry => (
                                    <div
                                        key={entry.key}
                                        className={styles.activityBarColumn}
                                        title={`${entry.label}: ${entry.value} sessions`}
                                        style={{
                                            ...(entry.value > 0 ? { cursor: 'pointer' } : undefined),
                                            ...(pulseBarModal?.point.key === entry.key ? {
                                                filter: 'drop-shadow(0 0 6px rgba(108, 52, 239, 0.7))',
                                                borderRadius: 4,
                                            } : undefined),
                                        }}
                                        onClick={e => {
                                            if (entry.value === 0) {return;}
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setPulseBarModal({
                                                point: entry,
                                                anchor: { top: rect.top + window.scrollY, left: rect.left + window.scrollX, width: rect.width, height: rect.height },
                                            });
                                        }}
                                    >
                                        {entry.value > 0 && (
                                            <div className={styles.activityBarCount}>{entry.value}</div>
                                        )}
                                        {pulseBreakdown && entry.value > 0 ? (
                                            <div
                                                className={styles.activityBarStacked}
                                                style={{ height: `${Math.max(12, (entry.value / maxFilteredPulseValue) * 140)}px` }}
                                            >
                                                {([
                                                    { key: 'completed', color: theme.colors.green, count: entry.completed },
                                                    { key: 'ongoing', color: theme.colors.blue_primary, count: entry.ongoing },
                                                    { key: 'abandoned', color: theme.colors.grey_text, count: entry.abandoned },
                                                    { key: 'aborted', color: '#d18100', count: entry.aborted },
                                                    { key: 'failed', color: theme.colors.red, count: entry.failed },
                                                ] as const).map(({ key: sKey, color, count }) => count > 0 && (
                                                    <div
                                                        key={sKey}
                                                        className={styles.activityBarSegment}
                                                        style={{
                                                            height: `${(count / entry.value) * 100}%`,
                                                            background: color,
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className={styles.activityBarValue} style={{ height: `${Math.max(12, (entry.value / maxFilteredPulseValue) * 140)}px`, opacity: entry.value === 0 ? 0.2 : 1 }} />
                                        )}
                                        <div className={styles.activityBarLabel}>{entry.label}</div>
                                    </div>
                                ))}
                            </div>
                            {pulseBreakdown && (
                                <div className={styles.pulseLegend}>
                                    {([
                                        { key: 'completed', label: 'Completed', color: theme.colors.green },
                                        { key: 'failed', label: 'Failed', color: theme.colors.red },
                                        { key: 'aborted', label: 'Stopped', color: '#d18100' },
                                        { key: 'abandoned', label: 'Abandoned', color: theme.colors.grey_text },
                                        { key: 'ongoing', label: 'In progress', color: theme.colors.blue_primary },
                                    ] as const).map(({ key, label: lbl, color }) => (
                                        <button
                                            key={lbl}
                                            type="button"
                                            className={styles.pulseLegendItem}
                                            style={{ opacity: pulseFilters.has(key) ? 1 : 0.3 }}
                                            onClick={() => setPulseFilters(prev => {
                                                const next = new Set(prev);
                                                if (next.has(key)) {next.delete(key);} else {next.add(key);}

                                                return next;
                                            })}
                                        >
                                            <div className={styles.pulseLegendDot} style={{ background: color }} />
                                            {lbl}
                                        </button>
                                    ))}
                                    <div className={styles.pulseLegendCtrl}>
                                        <button
                                            type="button"
                                            className={styles.pulseLegendCtrlBtn}
                                            onClick={() => setPulseFilters(new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']))}
                                        >All</button>
                                        <button
                                            type="button"
                                            className={styles.pulseLegendCtrlBtn}
                                            onClick={() => setPulseFilters(new Set())}
                                        >None</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Session health</div>
                                <div className={styles.panelSubtitle}>Failure, abandonment, and in-progress counts are separated from completions.</div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.healthList}>
                                {healthRows.map(row => (
                                    <div key={row.label} className={styles.healthRow}>
                                        <div className={styles.healthLabel}>{row.label}</div>
                                        <div className={styles.track}>
                                            <div className={styles.fill} style={{ width: `${model.totalExecutions ? (row.value / model.totalExecutions) * 100 : 0}%`, background: row.color }} />
                                        </div>
                                        <div className={styles.healthValue}>{row.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.dashboardGrid}>
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Top procedures</div>
                                <div className={styles.panelSubtitle}>Jump directly into the insights for the procedures pulling the most traffic.</div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.list}>
                                {model.topProcedures.map(procedure => (
                                    <div key={procedure.entityId} className={styles.listRowInteractive} onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${procedure.entityId}/insights`, { state: { fromNeoOverview: true } })}>
                                        <div className={styles.listMain}>
                                            <button type="button" className={styles.linkButton} onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${procedure.entityId}/insights`, { state: { fromNeoOverview: true } })}>
                                                <span className={styles.listTitle}>{procedure.name}</span>
                                            </button>
                                            <div className={styles.listMeta}>
                                                {procedure.version && <span>{`Version ${procedure.version}`}</span>}
                                                <span>{`Avg ${formatDuration(procedure.avgDuration)}`}</span>
                                            </div>
                                            <div className={styles.procedureBarTrack}>
                                                <div className={styles.procedureBarFill} style={{ width: `${(procedure.count / Math.max(model.topProcedures[0]?.count ?? 1, 1)) * 100}%` }} />
                                            </div>
                                        </div>
                                        <div className={styles.listCount}>{procedure.count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Top users</div>
                                <div className={styles.panelSubtitle}>Jump from the dashboard straight into the insights of the busiest users.</div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.list}>
                                {visibleTopActors.map(actor => (
                                    <div key={actor.actorId} className={styles.listRowInteractive} onClick={() => navigate(`/${RoutePaths.users}/analytics/${actor.actorId}/insights`, { state: { fromNeoOverview: true } })}>
                                        <div className={styles.listMain}>
                                            <button type="button" className={styles.linkButton} onClick={() => navigate(`/${RoutePaths.users}/analytics/${actor.actorId}/insights`, { state: { fromNeoOverview: true } })}>
                                                <span className={styles.listTitle}>{actor.displayName}</span>
                                            </button>
                                            <div className={styles.listMeta}>
                                                {actor.email && <span>{actor.email}</span>}
                                                {actor.visibleRoles.map(role => (
                                                    <span key={role} className={styles.roleBadge}>{role}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className={styles.listCount}>{actor.totalExecutions}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.dashboardGrid}>
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Operational pressure points</div>
                                <div className={styles.panelSubtitle}>
                                    {state.issuesLoading
                                        ? 'Issue metrics are loading separately so the main dashboard can render first.'
                                        : 'Issue metrics come from bounded full-data execution queries and stay disabled for the broadest scope.'}
                                </div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            {(range === 'all' && !selectedGroupId) ? (
                                <div className={styles.supportText}>Pick a smaller time window or a group to see longest-step and failed-step insights.</div>
                            ) : state.issuesLoading ? (
                                <div>
                                    {Array.from({ length: 5 }).map((_, index) => (
                                        <Skeleton key={index} variant="rounded" width="100%" height={42} style={{ marginBottom: 10 }} />
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.issueGrid}>
                                    <div className={styles.issueColumn}>
                                        <div className={styles.issueColumnHeader}>
                                            <div className={styles.issueTitle}>Longest steps</div>
                                        </div>
                                        <div className={styles.issueList}>
                                            {model.issues.longestSteps.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.entityId && item.executionId ? (
                                                        <button type="button" className={styles.linkButton} onClick={() => openExecutionDetails(item.entityId, item.executionId)}>
                                                            <span className={styles.issueName}>{item.name}</span>
                                                        </button>
                                                    ) : <span className={styles.issueName}>{item.name}</span>}
                                                    <span className={styles.issueValue}>{formatDuration(item.averageDuration)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.issueColumn}>
                                        <div className={styles.issueColumnHeader}>
                                            <div className={styles.issueTitle}>Most failed steps</div>
                                        </div>
                                        <div className={styles.issueList}>
                                            {model.issues.failedSteps.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.entityId && item.executionId ? (
                                                        <button type="button" className={styles.linkButton} onClick={() => openExecutionDetails(item.entityId, item.executionId)}>
                                                            <span className={styles.issueName}>{item.name}</span>
                                                        </button>
                                                    ) : <span className={styles.issueName}>{item.name}</span>}
                                                    <span className={styles.issueValue}>{`${item.count} • ${formatWholePercent((item.failureRate ?? 0) * 100)}`}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.issueColumn}>
                                        <div className={styles.issueColumnHeader}>
                                            <div className={styles.issueTitle}>Most failure-prone procedures</div>
                                        </div>
                                        <div className={styles.issueList}>
                                            {model.issues.failingProcedures.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.entityId ? (
                                                        <button type="button" className={styles.linkButton} onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${item.entityId}/insights`, { state: { fromNeoOverview: true } })}>
                                                            <span className={styles.issueName}>{item.name}</span>
                                                        </button>
                                                    ) : <span className={styles.issueName}>{item.name}</span>}
                                                    <span className={styles.issueValue}>{`${item.count} failed`}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.issueColumn}>
                                        <div className={styles.issueColumnHeader}>
                                            <div className={styles.issueTitle}>Most abandoned steps</div>
                                        </div>
                                        <div className={styles.issueList}>
                                            {model.issues.abandonedSteps.length === 0 ? (
                                                <span className={styles.issueValue}>No abandoned step data</span>
                                            ) : model.issues.abandonedSteps.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.entityId && item.executionId ? (
                                                        <button type="button" className={styles.linkButton} onClick={() => openExecutionDetails(item.entityId, item.executionId)}>
                                                            <span className={styles.issueName}>{item.name}</span>
                                                        </button>
                                                    ) : <span className={styles.issueName}>{item.name}</span>}
                                                    <span className={styles.issueValue}>{`${item.count}×`}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.issueColumn}>
                                        <div className={styles.issueColumnHeader}>
                                            <div className={styles.issueTitle}>Most stopped procedures</div>
                                        </div>
                                        <div className={styles.issueList}>
                                            {model.issues.abortedProcedures.length === 0 ? (
                                                <span className={styles.issueValue}>No stopped procedure data</span>
                                            ) : model.issues.abortedProcedures.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.entityId ? (
                                                        <button type="button" className={styles.linkButton} onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${item.entityId}/insights`, { state: { fromNeoOverview: true } })}>
                                                            <span className={styles.issueName}>{item.name}</span>
                                                        </button>
                                                    ) : <span className={styles.issueName}>{item.name}</span>}
                                                    <span className={styles.issueValue}>{`${item.count} stopped`}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.issueColumn}>
                                        <div className={styles.issueColumnHeader}>
                                            <div className={styles.issueTitle}>Most abandoned procedures</div>
                                        </div>
                                        <div className={styles.issueList}>
                                            {model.issues.abandonedProcedures.length === 0 ? (
                                                <span className={styles.issueValue}>No abandoned procedure data</span>
                                            ) : model.issues.abandonedProcedures.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.entityId ? (
                                                        <button type="button" className={styles.linkButton} onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${item.entityId}/insights`, { state: { fromNeoOverview: true } })}>
                                                            <span className={styles.issueName}>{item.name}</span>
                                                        </button>
                                                    ) : <span className={styles.issueName}>{item.name}</span>}
                                                    <span className={styles.issueValue}>{`${item.count} abandoned`}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Mix and recent sessions</div>
                                <div className={styles.panelSubtitle}>Mode and platform mix, plus the latest sessions in the current scope.</div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.chipRow}>
                                {model.modeMix.map(([label, value]) => (
                                    <div key={`mode-${label}`} className={styles.chip}>
                                        <span>{label}</span>
                                        <span className={styles.chipValue}>{value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.chipRow}>
                                {model.platformMix.map(([label, value]) => (
                                    <div key={`platform-${label}`} className={styles.chip}>
                                        <span>{label}</span>
                                        <span className={styles.chipValue}>{value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.recentGrid}>
                                {model.recentExecutions.map(execution => {
                                    const tone = getStatusTone(execution.statusLabel, theme);

                                    return (
                                        <div key={execution.id} className={styles.recentCardInteractive} onClick={() => openExecutionDetails(execution.entityId, execution.id)}>
                                            <button type="button" className={styles.linkButton} onClick={() => openExecutionDetails(execution.entityId, execution.id)}>
                                                <span className={styles.recentTitle}>{execution.entityName}</span>
                                            </button>
                                            <div className={styles.recentMetaChips}>
                                                <span className={styles.recentMetaChip}>{formatDateLabel(execution.startTime)}</span>
                                                <span className={styles.recentMetaChip}>{execution.actorLabel}</span>
                                                {execution.entityVersion && <span className={styles.recentMetaChip}>{`Version ${execution.entityVersion}`}</span>}
                                                <span className={styles.recentMetaChip}>{execution.executionMode}</span>
                                                <span className={styles.recentMetaChip}>{execution.platform}</span>
                                            </div>
                                            <div className={styles.chipRow}>
                                                <span className={styles.statusPill} style={{ color: tone.color, background: tone.background }}>{execution.statusLabel}</span>
                                                {execution.grading && (
                                                    <span className={styles.statusPill} style={{ color: theme.colors.violet_primary, background: theme.colors.transp_violet }}>
                                                        {execution.grading.isReviewed ? `Reviewed ${execution.grading.reviewCount}x` : 'Graded'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Pulse-bar detail popup ── */}
            {pulseBarModal && (() => {
                const { point, anchor } = pulseBarModal;
                const POPOVER_WIDTH = 380;
                const POPOVER_OFFSET = 8;
                const barCenterY = anchor.top + anchor.height / 2;
                const topRaw = barCenterY - 80;
                const top = Math.max(window.scrollY + 8, Math.min(topRaw, window.scrollY + window.innerHeight - window.innerHeight * 0.55 - 8));
                const rightLeft = anchor.left + anchor.width + POPOVER_OFFSET;
                const hasRightSpace = rightLeft + POPOVER_WIDTH <= window.scrollX + window.innerWidth - 8;
                const side: 'right' | 'left' = hasRightSpace ? 'right' : 'left';
                const left = side === 'right' ? rightLeft : anchor.left - POPOVER_WIDTH - POPOVER_OFFSET;
                const arrowTop = barCenterY - top - 7;
                const allExecutions = (state.payload ?? lastPayload)?.executions ?? [];
                const allActors = (state.payload ?? lastPayload)?.actors ?? [];
                const bucketExecutions = allExecutions
                    .filter(e => {
                        if (!point.executionIds.includes(e.id)) {return false;}
                        const lc = getExecutionLifecycleState(e);
                        const k = lc === 'completed' ? 'completed' : lc === 'failed' ? 'failed' : lc === 'aborted' ? 'aborted' : lc === 'abandoned' ? 'abandoned' : 'ongoing';

                        return popupFilters.has(k);
                    })
                    .sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? ''));
                const rawPoint = pulseSeries.find(p => p.key === point.key) ?? point;

                return ReactDOM.createPortal(
                    <>
                        <div className={styles.chipPopoverBackdrop} onClick={() => setPulseBarModal(null)} />
                        <div
                            className={styles.chipPopoverBox}
                            style={{ top, left, width: POPOVER_WIDTH }}
                            onClick={e => e.stopPropagation()}
                        >
                            {side === 'right'
                                ? <div className={styles.pulseBarArrowLeft} style={{ top: arrowTop }} />
                                : <div className={styles.pulseBarArrowRight} style={{ top: arrowTop }} />}
                            <div className={styles.pulseBarPopupAccent} />
                            <div className={styles.pulseBarPopupHeaderContent}>
                                <div className={styles.pulseBarPopupHeaderTop}>
                                    <div>
                                        <div className={styles.pulseBarPopupDate}>{point.label}</div>
                                        <div className={styles.pulseBarPopupCount}>{`${rawPoint.value} session${rawPoint.value !== 1 ? 's' : ''}`}</div>
                                    </div>
                                    <button type="button" className={styles.chipModalClose} onClick={() => setPulseBarModal(null)}>✕</button>
                                </div>
                                <div className={styles.pulseBarPopupSummaryRow}>
                                    {([
                                        { key: 'completed', label: 'Completed', count: rawPoint.completed, color: theme.colors.green },
                                        { key: 'failed', label: 'Failed', count: rawPoint.failed, color: theme.colors.red },
                                        { key: 'aborted', label: 'Stopped', count: rawPoint.aborted, color: '#d18100' },
                                        { key: 'abandoned', label: 'Abandoned', count: rawPoint.abandoned, color: theme.colors.grey_text },
                                        { key: 'ongoing', label: 'In progress', count: rawPoint.ongoing, color: theme.colors.blue_primary },
                                    ] as const).filter(p => p.count > 0).map(({ key: k, label: lbl, count, color }) => (
                                        <button
                                            key={k}
                                            type="button"
                                            className={styles.pulseBarPopupSummaryPill}
                                            style={{ opacity: popupFilters.has(k) ? 1 : 0.4 }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setPopupFilters(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(k)) {next.delete(k);} else {next.add(k);}

                                                    return next;
                                                });
                                            }}
                                        >
                                            <div className={styles.pulseBarPopupSummaryDot} style={{ background: color }} />
                                            {`${count} ${lbl}`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.pulseBarPopupScroller}>
                                <div className={styles.pulseBarPopupList}>
                                    {bucketExecutions.map(execution => {
                                        const status = getExecutionStatusLabel(execution);
                                        const tone = getStatusTone(status, theme);
                                        const resolvedActors = execution.actorIds.map(actorId => allActors.find(a => a.id === actorId));
                                        const actorNames = resolvedActors
                                            .map(actor => actor?.displayName || actor?.email || 'Unknown actor')
                                            .join(', ') || 'Unknown actor';
                                        const primaryActor = resolvedActors[0];
                                        const actorEmail = resolvedActors.length === 1 && primaryActor?.email && primaryActor.email !== primaryActor.displayName ? primaryActor.email : null;
                                        const initials = execution.entityName
                                            .split(' ')
                                            .filter(Boolean)
                                            .slice(0, 2)
                                            .map(w => w[0]?.toUpperCase() ?? '')
                                            .join('');

                                        return (
                                            <div
                                                key={execution.id}
                                                className={styles.pulseBarPopupRow}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => {
                                                    setPulseBarModal(null);
                                                    openExecutionDetails(execution.entityId, execution.id);
                                                }}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        setPulseBarModal(null);
                                                        openExecutionDetails(execution.entityId, execution.id);
                                                    }
                                                }}
                                            >
                                                <div className={styles.pulseBarPopupAvatar}>{initials}</div>
                                                <div className={styles.pulseBarPopupRowMain}>
                                                    <div className={styles.pulseBarPopupName}>{execution.entityName}</div>
                                                    <div className={styles.pulseBarPopupRowSub}>{actorNames}</div>
                                                    {actorEmail && (
                                                        <div className={styles.pulseBarPopupRowSub}>{actorEmail}</div>
                                                    )}
                                                    {execution.startTime && (
                                                        <div className={styles.pulseBarPopupRowSub}>
                                                            {new Date(execution.startTime).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                                                            {' · '}
                                                            {new Date(execution.startTime).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    )}
                                                    {execution.durationSeconds !== null && (
                                                        <div className={styles.pulseBarPopupRowSub}>{formatDuration(execution.durationSeconds)}</div>
                                                    )}
                                                </div>
                                                <div className={styles.pulseBarPopupBadges}>
                                                    <span
                                                        className={styles.pulseBarPopupBadge}
                                                        style={{ color: theme.colors.grey_text, background: theme.colors.grey_light }}
                                                    >
                                                        {execution.executionMode}
                                                    </span>
                                                    <span
                                                        className={styles.pulseBarPopupBadge}
                                                        style={{ color: tone.color, background: tone.background }}
                                                    >
                                                        {status}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>,
                    document.body,
                );
            })()}
        </>
    );
};